<template>
  <div v-if="detail" class="pd-page page-body">
    <div class="pd-container">
      <nav class="pd-breadcrumb" aria-label="面包屑">
        <a href="#" class="pd-breadcrumb__link" @click.prevent="goHome">首页</a>
        <span class="pd-breadcrumb__sep">/</span>
        <a href="#" class="pd-breadcrumb__link" @click.prevent="goCategory">产品中心</a>
        <span class="pd-breadcrumb__sep">/</span>
        <a href="#" class="pd-breadcrumb__link" @click.prevent="goCategory">{{ detail.category.label }}</a>
        <span class="pd-breadcrumb__sep">/</span>
        <span class="pd-breadcrumb__current">{{ detail.product.name }}</span>
      </nav>

      <section class="pd-hero">
        <div class="pd-hero__gallery">
          <button
            v-if="galleryImages.length > 1"
            type="button"
            class="pd-gallery-btn pd-gallery-btn--prev"
            aria-label="上一张"
            @click="prevImage"
          >
            ‹
          </button>
          <div class="pd-hero__img-wrap" :class="{ 'pd-hero__img-wrap--ui': isSystemProduct }">
            <img
              :src="galleryImages[activeImageIndex]"
              :alt="detail.product.name"
              @error="onGalleryImgError"
            />
          </div>
          <button
            v-if="galleryImages.length > 1"
            type="button"
            class="pd-gallery-btn pd-gallery-btn--next"
            aria-label="下一张"
            @click="nextImage"
          >
            ›
          </button>
        </div>

        <div class="pd-hero__info">
          <p class="pd-hero__series">{{ detail.category.label }}</p>
          <h1 class="pd-hero__title">{{ detail.product.name }}</h1>
          <p class="pd-hero__model">{{ detail.product.model }}</p>
          <p class="pd-hero__tagline">{{ meta.tagline }}</p>
          <div v-if="detail.product.isCustomProduct && !clientDemo" class="pd-hero__admin">
            <button type="button" class="btn btn--secondary pd-hero__edit" @click="goEditProduct">编辑上架信息</button>
          </div>
          <a v-if="!isSystemProduct" href="#" class="pd-download" @click.prevent>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M5 20h14v-2H5v2zm7-18l-5.5 5.5 1.42 1.42L11 6.17V16h2V6.17l3.08 3.09 1.42-1.42L12 2z" />
            </svg>
            下载产品资料
          </a>

          <ul class="pd-feature-list">
            <li v-for="(feat, i) in meta.featureItems" :key="i">
              <span class="pd-feature-list__icon">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path :d="featureIconPaths[feat.icon]" />
                </svg>
              </span>
              <div>
                <strong>{{ feat.title }}</strong>
                <p>{{ feat.desc }}</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section v-if="meta?.intro" class="pd-intro">
        <h2 class="pd-section-title">产品介绍</h2>
        <p class="pd-intro__text">{{ meta.intro }}</p>
        <ul v-if="meta.introPoints?.length" class="pd-intro__points">
          <li v-for="(point, i) in meta.introPoints" :key="i">{{ point }}</li>
        </ul>
      </section>

      <section v-if="meta?.applications?.length" class="pd-applications">
        <h2 class="pd-section-title">典型应用场景</h2>
        <div class="pd-applications__grid">
          <article v-for="(app, i) in meta.applications" :key="i" class="pd-app-card">
            <h3>{{ app.title }}</h3>
            <p>{{ app.desc }}</p>
          </article>
        </div>
      </section>

      <section class="pd-params">
        <h2 class="pd-section-title">产品参数</h2>
        <div class="pd-params-grid">
          <div
            v-for="(item, i) in meta.highlights"
            :key="i"
            class="pd-param-item"
          >
            <span class="pd-param-item__icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path :d="paramIconPaths[item.icon]" />
              </svg>
            </span>
            <strong class="pd-param-item__value">{{ item.value }}</strong>
            <span class="pd-param-item__label">{{ item.label }}</span>
          </div>
        </div>
      </section>

      <section class="pd-specs">
        <div class="pd-specs__tabs">
          <button
            v-for="tab in meta.specTabs"
            :key="tab"
            type="button"
            class="pd-specs__tab"
            :class="{ 'pd-specs__tab--active': activeSpecTab === tab }"
            @click="activeSpecTab = tab"
          >
            {{ tab }}
          </button>
        </div>

        <div class="pd-spec-table-wrap">
          <table class="pd-spec-table">
            <thead>
              <tr>
                <th>参数分类</th>
                <th>参数名称</th>
                <th>参数值</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in meta.specGroups" :key="group.name">
                <tr
                  v-for="(row, ri) in group.rows"
                  :key="group.name + ri"
                >
                  <td v-if="ri === 0" :rowspan="group.rows.length" class="pd-spec-group">
                    {{ group.name }}
                  </td>
                  <td>{{ row[0] }}</td>
                  <td>{{ row[1] }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="systemScreenshots.length" id="pd-system-ui" class="pd-system-shots">
        <h2 class="pd-section-title">系统界面</h2>
        <p class="pd-system-shots__hint">
          以下为平台典型功能界面；截图文件可放在 <code>public/images/systems/</code> 目录（见 README）。
        </p>
        <div class="pd-system-shots__grid">
          <article v-for="(shot, idx) in systemScreenshots" :key="shot.title" class="pd-system-shot">
            <button type="button" class="pd-system-shot__img" @click="openShotLightbox(idx)">
              <img
                :src="shotSrc(shot)"
                :alt="shot.title"
                loading="lazy"
                @error="(e) => onShotImgError(e, shot)"
              />
            </button>
            <h3>{{ shot.title }}</h3>
            <p>{{ shot.desc }}</p>
          </article>
        </div>
      </section>

      <section v-if="meta?.deliveryItems?.length" class="pd-delivery">
        <h2 class="pd-section-title">交付与服务</h2>
        <ol class="pd-delivery__list">
          <li v-for="(item, i) in meta.deliveryItems" :key="i" class="pd-delivery__item">
            <span class="pd-delivery__step">{{ String(i + 1).padStart(2, '0') }}</span>
            <div>
              <strong>{{ item.title }}</strong>
              <p>{{ item.desc }}</p>
            </div>
          </li>
        </ol>
      </section>

      <section v-if="relatedProducts.length" class="pd-related">
        <h2 class="pd-section-title">相关产品</h2>
        <div class="pd-related-grid">
          <a
            v-for="item in relatedProducts"
            :key="item.slug"
            href="#"
            class="pd-related-card"
            @click.prevent="goProduct(item.slug)"
          >
            <div class="pd-related-card__img">
              <img :src="item.image" :alt="item.name" />
            </div>
            <p class="pd-related-card__name">{{ item.name }}</p>
          </a>
        </div>
      </section>
    </div>

    <div v-if="shotLightboxIndex !== null" class="pd-lightbox" @click.self="closeShotLightbox">
      <button type="button" class="pd-lightbox__close" @click="closeShotLightbox">×</button>
      <button type="button" class="pd-lightbox__prev" @click="prevShot">‹</button>
      <img
        v-if="lightboxShot"
        :src="shotSrc(lightboxShot, shotFallbackKeys[lightboxShot.title])"
        :alt="lightboxShot.title"
        @error="(e) => onShotImgError(e, lightboxShot)"
      />
      <button type="button" class="pd-lightbox__next" @click="nextShot">›</button>
      <p class="pd-lightbox__caption">{{ lightboxShot?.title }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  buildProductDetailMeta,
  defaultCategoryId,
  getRelatedProducts as getBuiltInRelatedProducts,
} from '../data/productCatalog'
import { findProduct, getRelatedCustomProducts } from '../utils/customProductCatalog'
import { CLIENT_DEMO_MODE } from '../config/demoMode'
import { shotDisplaySrc } from '../data/systemsCatalog'

const clientDemo = CLIENT_DEMO_MODE

const route = useRoute()
const router = useRouter()

const activeImageIndex = ref(0)
const activeSpecTab = ref('标准版')
const shotLightboxIndex = ref(null)
const shotFallbackKeys = reactive({})

const detail = computed(() => {
  const categoryId = route.params.categoryId
  const productSlug = route.params.productSlug
  if (!categoryId || productSlug == null) return null
  return findProduct(categoryId, productSlug)
})

const meta = computed(() => {
  if (!detail.value) return null
  return buildProductDetailMeta(detail.value.category, detail.value.product)
})

const isSystemProduct = computed(() => detail.value?.product.productKind === 'system')

const isSystemModule = computed(() => !!detail.value?.product.isSystemModule)

const systemScreenshots = computed(() => {
  if (isSystemModule.value) return []
  return detail.value?.product.screenshots || []
})

const galleryImages = computed(() => {
  if (!detail.value) return []
  const p = detail.value.product
  // 自研系统：主图只展示一张封面，多张界面在下方「系统界面」区
  if (p.productKind === 'system') {
    if (p.image) return [p.image]
    if (p.imageFallback) return [p.imageFallback]
    return []
  }
  if (p.gallery?.length) return p.gallery
  if (p.image) return [p.image]
  if (p.imageFallback) return [p.imageFallback]
  return []
})

const lightboxShot = computed(() => {
  if (shotLightboxIndex.value == null) return null
  return systemScreenshots.value[shotLightboxIndex.value] || null
})

function shotSrc(shot, forceFallback) {
  return shotDisplaySrc(shot, forceFallback || shotFallbackKeys[shot.title])
}

function onShotImgError(e, shot) {
  shotFallbackKeys[shot.title] = true
  if (shot.fallback) e.target.src = shot.fallback
}

function onGalleryImgError(e) {
  const p = detail.value?.product
  if (!p) return
  const fb = p.imageFallback || p.screenshots?.[0]?.fallback
  if (fb && e.target.src !== fb) e.target.src = fb
}

function openShotLightbox(idx) {
  shotLightboxIndex.value = idx
  document.body.style.overflow = 'hidden'
}

function closeShotLightbox() {
  shotLightboxIndex.value = null
  document.body.style.overflow = ''
}

function prevShot() {
  const n = systemScreenshots.value.length
  if (!n) return
  shotLightboxIndex.value = (shotLightboxIndex.value - 1 + n) % n
}

function nextShot() {
  const n = systemScreenshots.value.length
  if (!n) return
  shotLightboxIndex.value = (shotLightboxIndex.value + 1) % n
}

const relatedProducts = computed(() => {
  if (!detail.value) return []
  const { category, product } = detail.value
  const builtIn = getBuiltInRelatedProducts(category.id, product.slug, 4)
  if (builtIn.length) return builtIn
  return getRelatedCustomProducts(category.id, product.slug, 4)
})

function goEditProduct() {
  if (!detail.value?.product?.slug) return
  router.push({
    name: 'product-admin',
    query: { edit: detail.value.product.slug },
  })
}

const featureIconPaths = {
  vision:
    'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  nav:
    'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  controller:
    'M4 6h16v12H4V6zm2 2v8h12V8H6zm2 2h8v4H8v-4z',
  software:
    'M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z',
  sensor:
    'M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z',
  cloud:
    'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z',
}

const paramIconPaths = {
  load: 'M13 2.05v3.03c3.39.49 6 3.39 6 6.92 0 .9-.18 1.75-.48 2.54l2.6 1.53c.56-1.24.88-2.62.88-4.07 0-5.18-3.95-9.45-9-9.95zM12 19c-3.87 0-7-3.13-7-7 0-3.53 2.61-6.43 6-6.92V2.05c-5.06.5-9 4.76-9 9.95 0 5.52 4.47 10 9.99 10 3.31 0 6.24-1.61 8.06-4.09l-2.6-1.53A7.024 7.024 0 0112 19z',
  model: 'M3 5v14h18V5H3zm16 12H5V7h14v10zM7 9h2v2H7V9zm0 4h2v2H7v-2zm4-4h6v2h-6V9zm0 4h6v2h-6v-2z',
  type: 'M12 2l2.4 4.8L20 8l-3.6 3.5.9 5.5L12 15l-5.3 2 .9-5.5L4 8l5.6-1.2L12 2z',
  safe: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 16l-4-4 1.41-1.41L11 14.17l6.59-6.59L19 9l-8 8z',
  service: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
}

function prevImage() {
  const len = galleryImages.value.length
  if (len <= 1) return
  activeImageIndex.value = (activeImageIndex.value - 1 + len) % len
}

function nextImage() {
  const len = galleryImages.value.length
  if (len <= 1) return
  activeImageIndex.value = (activeImageIndex.value + 1) % len
}

function goHome() {
  router.push({ name: 'home' })
}

function goCategory() {
  const categoryId = detail.value?.category.id || defaultCategoryId
  router.push({ name: 'products', params: { categoryId } })
}

function goProduct(slug) {
  router.push({
    name: 'product-detail',
    params: {
      categoryId: detail.value.category.id,
      productSlug: slug,
    },
  })
}

watch(
  () => route.params.productSlug,
  () => {
    activeImageIndex.value = 0
    activeSpecTab.value = '标准版'
    closeShotLightbox()
  }
)
</script>

<style scoped>
.pd-page {
  background: transparent;
  min-height: calc(100vh - 72px);
  padding: 24px 0 80px;
}

.pd-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.pd-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 28px;
  font-size: 13px;
  color: #888;
}

.pd-breadcrumb__link {
  color: #666;
  text-decoration: none;
}

.pd-breadcrumb__link:hover {
  color: var(--brand-primary);
}

.pd-breadcrumb__sep {
  color: #ccc;
}

.pd-breadcrumb__current {
  color: #333;
}

.pd-hero {
  display: flex;
  gap: 48px;
  align-items: flex-start;
  margin-bottom: 40px;
}

.pd-hero__gallery {
  position: relative;
  width: 520px;
  flex-shrink: 0;
}

.pd-hero__img-wrap {
  height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.pd-hero__img-wrap img {
  max-width: 90%;
  max-height: 380px;
  object-fit: contain;
}

.pd-hero__img-wrap--ui {
  background: linear-gradient(160deg, #f0f4fa 0%, #e4ecf6 100%);
  border-color: #e8eaed;
}

.pd-hero__img-wrap--ui img {
  max-width: 100%;
  width: 100%;
  object-fit: contain;
  object-position: top center;
}

.pd-gallery-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #666;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  z-index: 1;
}

.pd-gallery-btn:hover {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
}

.pd-gallery-btn--prev {
  left: 12px;
}

.pd-gallery-btn--next {
  right: 12px;
}

.pd-hero__info {
  flex: 1;
  min-width: 0;
  padding-top: 8px;
}

.pd-hero__series {
  margin: 0 0 8px;
  font-size: 13px;
  color: #888;
}

.pd-hero__title {
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 700;
  color: #222;
  line-height: 1.35;
}

.pd-hero__model {
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 600;
  color: var(--brand-primary);
}

.pd-hero__tagline {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.7;
  color: #666;
}

.pd-hero__admin {
  margin-bottom: 16px;
}

.pd-hero__edit {
  font-size: 13px;
  padding: 8px 16px;
}

.pd-download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 28px;
  font-size: 13px;
  color: var(--brand-primary);
  text-decoration: none;
}

.pd-download:hover {
  text-decoration: underline;
}

.pd-download--primary {
  display: inline-flex;
  padding: 10px 16px;
  margin-bottom: 8px;
  color: #fff;
  background: var(--brand-primary);
  border-radius: 6px;
  font-weight: 600;
}

.pd-download--primary:hover {
  text-decoration: none;
  background: #1c4a9e;
}

.pd-feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid #eee;
}

.pd-feature-list li {
  display: flex;
  gap: 14px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.pd-feature-list__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2fb;
  color: var(--brand-primary);
  border-radius: 6px;
}

.pd-feature-list strong {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #222;
}

.pd-feature-list p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #888;
}

.pd-section-title {
  margin: 0 0 28px;
  font-size: clamp(22px, 2.5vw, 28px);
  font-weight: 800;
  text-align: center;
  color: var(--text-primary);
  letter-spacing: -0.3px;
}

.pd-section-title::after {
  content: '';
  display: block;
  width: 48px;
  height: 3px;
  margin: 12px auto 0;
  border-radius: var(--radius-pill);
  background: var(--gradient-brand);
}

.pd-intro {
  margin-bottom: 36px;
  padding: 28px 32px;
  border-radius: var(--radius-lg);
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
  box-shadow: var(--shadow-card);
}

.pd-intro__text {
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.85;
  color: var(--text-secondary);
}

.pd-intro__points {
  margin: 0;
  padding: 0 0 0 20px;
  font-size: 14px;
  line-height: 1.75;
  color: var(--text-secondary);
}

.pd-intro__points li + li {
  margin-top: 8px;
}

.pd-applications {
  margin-bottom: 44px;
}

.pd-applications__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.pd-app-card {
  padding: 24px;
  border-radius: var(--radius-lg);
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
  box-shadow: var(--shadow-card);
  transition: transform 0.25s var(--ease-premium), box-shadow 0.25s;
}

.pd-app-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-elevated);
}

.pd-app-card h3 {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: var(--brand-primary);
}

.pd-app-card p {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: var(--text-secondary);
}

.pd-delivery {
  margin-bottom: 56px;
  padding: 32px 36px;
  border-radius: var(--radius-lg);
  background: linear-gradient(135deg, rgba(23, 107, 137, 0.04) 0%, rgba(47, 166, 109, 0.03) 100%);
  border: 1px solid var(--border-soft);
}

.pd-delivery__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pd-delivery__item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: var(--radius-md);
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
}

.pd-delivery__step {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--gradient-brand);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.pd-delivery__item strong {
  display: block;
  margin-bottom: 4px;
  font-size: 15px;
  color: var(--text-primary);
}

.pd-delivery__item p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-secondary);
}

.pd-params {
  margin-bottom: 56px;
  padding: 48px 24px;
  border-radius: var(--radius-xl);
  background: linear-gradient(180deg, rgba(244, 248, 252, 0.9) 0%, rgba(237, 243, 251, 0.95) 100%);
  border: 1px solid var(--border-soft);
}

.pd-params-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  max-width: 960px;
  margin: 0 auto;
}

.pd-param-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
}

.pd-param-item__icon {
  color: var(--brand-primary);
  margin-bottom: 4px;
}

.pd-param-item__value {
  font-size: 20px;
  font-weight: 700;
  color: #222;
  word-break: break-all;
}

.pd-param-item__label {
  font-size: 12px;
  color: #999;
}

.pd-specs {
  margin-bottom: 56px;
}

.pd-specs__tabs {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 20px;
}

.pd-specs__tab {
  border: none;
  background: none;
  font-size: 13px;
  color: #888;
  cursor: pointer;
  padding: 6px 0;
}

.pd-specs__tab--active {
  color: var(--brand-primary);
  font-weight: 600;
  border-bottom: 2px solid var(--brand-primary);
}

.pd-spec-table-wrap {
  overflow-x: auto;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-soft);
  box-shadow: var(--shadow-sm);
}

.pd-spec-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.pd-spec-table thead th {
  background: var(--gradient-brand-dark);
  color: #fff;
  padding: 14px 18px;
  text-align: left;
  font-weight: 700;
}

.pd-spec-table tbody td {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  color: #444;
  vertical-align: top;
}

.pd-spec-table tbody tr:nth-child(even) td:not(.pd-spec-group) {
  background: #fafafa;
}

.pd-spec-group {
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
  width: 120px;
}

.pd-system-shots {
  margin-bottom: 56px;
  padding: 40px 0 48px;
  background: #f4f6f9;
}

.pd-system-shots__hint {
  margin: -16px auto 28px;
  max-width: 640px;
  text-align: center;
  font-size: 13px;
  color: #777;
  line-height: 1.6;
}

.pd-system-shots__hint code {
  font-size: 12px;
  background: #e8ecf2;
  padding: 2px 6px;
  border-radius: 4px;
}

.pd-system-shots__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px;
}

.pd-system-shot h3 {
  margin: 12px 16px 6px;
  font-size: 14px;
  color: #222;
}

.pd-system-shot p {
  margin: 0 16px 16px;
  font-size: 12px;
  line-height: 1.55;
  color: #888;
}

.pd-system-shot__img {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: linear-gradient(160deg, #f0f4fa 0%, #e4ecf6 100%);
  cursor: pointer;
}

.pd-system-shot__img img {
  width: 100%;
  height: 200px;
  object-fit: contain;
  object-position: top center;
  display: block;
}

.pd-lightbox {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.pd-lightbox img {
  max-width: 92vw;
  max-height: 80vh;
  object-fit: contain;
}

.pd-lightbox__close {
  position: absolute;
  top: 12px;
  right: 16px;
  border: none;
  background: none;
  color: #fff;
  font-size: 32px;
  cursor: pointer;
}

.pd-lightbox__prev,
.pd-lightbox__next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
}

.pd-lightbox__prev {
  left: 12px;
}

.pd-lightbox__next {
  right: 12px;
}

.pd-lightbox__caption {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.pd-related-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.pd-related-card {
  text-decoration: none;
  color: inherit;
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-lg);
  background: var(--surface-card);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.3s var(--ease-premium), transform 0.3s var(--ease-premium);
}

.pd-related-card:hover {
  box-shadow: var(--shadow-elevated);
  transform: translateY(-3px);
}

.pd-related-card__img {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #f8f9fb;
}

.pd-related-card__img img {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
}

.pd-related-card__name {
  margin: 0;
  padding: 12px;
  font-size: 13px;
  text-align: center;
  color: #333;
  line-height: 1.4;
}

@media (max-width: 1024px) {
  .pd-hero {
    flex-direction: column;
  }

  .pd-hero__gallery {
    width: 100%;
  }

  .pd-params-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .pd-related-grid,
  .pd-system-shots__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .pd-params-grid,
  .pd-related-grid,
  .pd-system-shots__grid {
    grid-template-columns: 1fr;
  }
}
</style>
